import * as React from 'react';
import styled from 'styled-components';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { PaneCancelButton } from './buttons/PaneCancelButton';
import { PaneSubmitButton } from './buttons/PaneSubmitButton';
import { PaneEditButton } from './buttons/PaneEditButton';
import { PaneBody } from './elements/PaneBody';
import { PaneFooter } from './elements/PaneFooter';
import { PaneTitle } from './elements/PaneTitle';
import { PaneHeading } from './elements/PaneHeading';
import { PaneSubheader } from './elements/PaneSubheader';
import { PaneFab } from './elements/PaneFab';
import { PaneGuide } from './elements/PaneGuide';
import { PANE_MAXWIDTH, PANE_MINWIDTH, PANE_NARROW, PANE_WIDE } from '../../types/Constants';
import { PaneSeparator } from './elements/PaneSeparator';

interface IProps {
  /** @ignore */
  className?: string;
  /** @ignore */
  children?: React.ReactNode;
}

const PaneBase = (props: IProps) => {
  const globalState = useGlobalState();

  return (
    <div className={props.className} style={{ width: globalState.expanded ? PANE_WIDE : PANE_NARROW }}>
      {props.children}
    </div>
  );
}

const PaneStyled = styled(PaneBase)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: ${p => p.theme.colors.primary[3]};
  transition: width ease-in-out ${p => p.theme.animation.duration}ms,
    opacity ease-in-out 0.5s;
  display: flex;
  flex-direction: column;  
  min-width: ${PANE_MINWIDTH};
  max-width: ${PANE_MAXWIDTH};

  // A div, which is taller than the pane itself, is placed behind. It is 
  // made extra tall to remove shadow attenuation at the corners from view.
  &:before {
    position: absolute;
    content: '';
    left: 0;
    top: -20px;
    right: 0;
    bottom: -20px;
    z-index: -1;
    box-shadow: 0px 0px 10px rgba(0,0,0,1.0);
  }
`

/**
 * A composite UI component that serves as a side pane in the application.
 * 
 * The `Pane` can optionally be expandable (toggling between collapsed and expanded widths),
 * and comes with several subcomponents for building its layout:
 * 
 * - `Pane.Header` — A top header area
 * - `Pane.Footer` — A bottom footer area
 * - `Pane.Content` — The main scrollable content area
 * - `Pane.Separator` — A visual divider between sections
 * - `Pane.Fab` — A floating action button inside the pane
 * 
 * Widths:
 * - Collapsed: 350px
 * - Expanded: 600px
 * 
 * @component
 * 
 * @param {string} [className] - Optional CSS class for custom styling.
 * @param {React.ReactNode} [children] - The pane's contents.
  * 
 * @example
 * ```tsx
 * <Pane expandable>
 *   <Pane.Header>Filters</Pane.Header>
 *   <Pane.Content>
 *     <FilterControls />
 *   </Pane.Content>
 *   <Pane.Footer>
 *     <Pane.Fab onClick={handleApply} />
 *   </Pane.Footer>
 * </Pane>
 * ```
 */
const Pane = (props: IProps) => <PaneStyled {...props}/>

// Elements:
Pane.Body = PaneBody;
Pane.Footer = PaneFooter;
Pane.Title = PaneTitle;
Pane.Heading = PaneHeading;
Pane.Subheader = PaneSubheader;
Pane.Separator = PaneSeparator;
Pane.Fab = PaneFab;
Pane.Guide = PaneGuide;

// Buttons:
Pane.CancelButton = PaneCancelButton;
Pane.SubmitButton = PaneSubmitButton;
Pane.EditButton = PaneEditButton;

export { Pane }

